import { useState } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Link,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { CloudRegular, OpenRegular, ShareRegular } from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { UserPicker } from '../features/users/UserPicker';
import { useApi } from '../api/useApi';
import { formatBytes } from '../util/bytes';
import type { DirectoryUserDto, ExternalShareResultDto, OneDriveUserDto } from '../api/types';

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium, padding: '10px 14px' }}>
      <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

export function OneDrivePage() {
  const api = useApi();
  const [user, setUser] = useState<DirectoryUserDto>();
  const [data, setData] = useState<OneDriveUserDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [shares, setShares] = useState<ExternalShareResultDto | null>(null);
  const [scanning, setScanning] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  function scan() {
    if (!user) return;
    setScanning(true);
    setShareError(null);
    api
      .scanOneDriveShares(user.id)
      .then(setShares)
      .catch((e) => setShareError(e instanceof Error ? e.message : String(e)))
      .finally(() => setScanning(false));
  }

  function revoke(itemId: string, permissionId: string) {
    if (!user) return;
    setRevoking(permissionId);
    api
      .revokeOneDriveShare(user.id, itemId, permissionId)
      .then(() => setShares((s) => (s ? { ...s, shares: s.shares.filter((x) => x.permissionId !== permissionId) } : s)))
      .catch((e) => setShareError(e instanceof Error ? e.message : String(e)))
      .finally(() => setRevoking(null));
  }

  function onSelect(u: DirectoryUserDto | undefined) {
    setUser(u);
    setData(null);
    setError(null);
    setShares(null);
    setShareError(null);
    if (!u) return;
    setLoading(true);
    api
      .getOneDriveUser(u.userPrincipalName)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }

  const a = data?.account;
  return (
    <div>
      <PageHeader title="OneDrive" description="Speicher und Nutzung des OneDrives eines Benutzers." />

      <div style={{ maxWidth: 640, margin: '0 auto 1.5rem' }}>
        <UserPicker fluid onSelect={onSelect} placeholder="Benutzer suchen…" />
      </div>

      {loading ? <Spinner label="Lade OneDrive…" /> : null}
      {error ? (
        <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
      ) : null}

      {data && !data.found ? (
        <MessageBar intent="info"><MessageBarBody>{data.note}</MessageBarBody></MessageBar>
      ) : null}

      {a ? (
        <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
          <div style={{ background: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: 12, padding: '1rem 1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <CloudRegular />
              <Text weight="semibold" size={400}>Übersicht — {user?.displayName}</Text>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text size={300}>Genutzt</Text>
              <Text size={300} weight="semibold">
                {formatBytes(a.storageUsedBytes)} von {formatBytes(a.storageAllocatedBytes)} ({a.percentUsed}%)
              </Text>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: tokens.colorNeutralBackground3, overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, a.percentUsed)}%`, height: '100%', background: a.percentUsed >= 85 ? tokens.colorPaletteRedForeground1 : tokens.colorBrandBackground }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 16 }}>
              <Stat label="Dateien" value={a.fileCount.toLocaleString('de-DE')} />
              <Stat label="Aktive Dateien (28 T)" value={a.activeFileCount.toLocaleString('de-DE')} />
              <Stat label="Letzte Aktivität" value={a.lastActivity ? new Date(a.lastActivity).toLocaleDateString('de-DE') : '—'} />
              <Stat label="Anteil an Tenant-Quota" value={`${data!.shareOfTenantPercent}%`} />
              <Stat label="Tenant gesamt genutzt" value={formatBytes(data!.tenantTotalUsedBytes)} />
            </div>

            {a.siteUrl ? (
              <div style={{ marginTop: 16 }}>
                <Link href={a.siteUrl} target="_blank" rel="noreferrer">
                  Im SharePoint öffnen <OpenRegular />
                </Link>
              </div>
            ) : null}
            <Caption1 style={{ display: 'block', marginTop: 8, color: tokens.colorNeutralForeground3 }}>
              Quelle: Microsoft-365-Nutzungsbericht (Stand 1–2 Tage alt).
            </Caption1>
          </div>

          <div style={{ background: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: 12, padding: '1rem 1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <ShareRegular />
              <Text weight="semibold" size={400} style={{ flex: 1 }}>Externe Freigaben</Text>
              <Button size="small" icon={scanning ? <Spinner size="tiny" /> : <ShareRegular />} disabled={scanning} onClick={scan}>
                {scanning ? 'Scanne…' : shares ? 'Erneut scannen' : 'Scannen'}
              </Button>
            </div>

            {shareError ? <MessageBar intent="error"><MessageBarBody>{shareError}</MessageBarBody></MessageBar> : null}

            {!shares && !scanning ? (
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                „Scannen" findet anonyme Links und Freigaben an externe Gäste/Personen — und macht sie gezielt widerrufbar.
              </Caption1>
            ) : null}

            {shares?.error ? (
              <MessageBar intent="warning"><MessageBarBody>{shares.error}</MessageBarBody></MessageBar>
            ) : null}

            {shares && !shares.error && shares.shares.length === 0 ? (
              <Body1>Keine externen Freigaben gefunden.</Body1>
            ) : null}

            {shares && shares.shares.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {shares.shares.map((s) => (
                  <div key={s.permissionId} style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8, borderTop: `0.5px solid ${tokens.colorNeutralStroke2}` }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Text size={300} weight="semibold" className="pii">{s.fileName}</Text>
                      <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3 }} className="pii">
                        {s.sharedWith}{s.path ? ` · ${s.path}` : ''}
                      </Caption1>
                    </div>
                    <Badge appearance="tint" color={s.kind === 'Anonymer Link' ? 'danger' : 'warning'}>{s.kind}</Badge>
                    <Button
                      size="small"
                      appearance="subtle"
                      disabled={revoking === s.permissionId}
                      onClick={() => revoke(s.itemId, s.permissionId)}
                      style={{ color: tokens.colorPaletteRedForeground1 }}
                    >
                      {revoking === s.permissionId ? 'Ziehe zurück…' : 'Zurückziehen'}
                    </Button>
                  </div>
                ))}
                {shares.truncated ? (
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, marginTop: 6 }}>
                    Scan begrenzt (sehr großes OneDrive) — Liste evtl. nicht vollständig.
                  </Caption1>
                ) : null}
              </div>
            ) : null}
          </div>

          <MessageBar intent="info">
            <MessageBarBody>
              <Badge appearance="tint" color="informative" size="small">geplant</Badge>{' '}
              Sharing-Report, Speicherlimit/Versionen ändern, Delegation und Wiederherstellung folgen — sie benötigen
              SharePoint-Admin-Schnittstellen über den reinen Graph hinaus.
            </MessageBarBody>
          </MessageBar>
        </div>
      ) : null}
    </div>
  );
}
